/**
 * @author: Codex
 * @date: 2026/6/15
 * @desc: 级联单选筛选器组件
 */
import React, { useState } from 'react';
import classNames from 'classnames';
import { Dropdown, Icon, Input } from 'tea-component';
import { useIntl } from 'umi';
import CommonBubble from '@home/components/CommonBubble';
import EllipsisHover from '@home/components/EllipsisHover';
import useCascaderSelect from './useCascaderSelect';
import {
  CASCADER_SELECT_LOCALE_KEYS,
  EMPTY_CONTEXT,
  formatLocaleText,
  getCascadedActivePath,
  getCascaderColumnTitle,
  getLeafChangeContext,
  getOptionDisplayText,
  getOptionPathFromColumns,
  getTextContent,
  hasOptionChildren,
} from './utils';

import './index.scss';

export interface ICascaderSelectOption {
  value: string;
  text: string | React.ReactNode;
  children?: ICascaderSelectOption[];
  disabled?: boolean;
  icon?: React.ReactNode | string;
  tooltip?: string | React.ReactNode;
}

export interface ICascaderSelectChangeContext {
  selectedOption?: ICascaderSelectOption;
  parentOption?: ICascaderSelectOption;
  path: ICascaderSelectOption[];
}

export interface ICascaderSelectOverallGroup {
  title?: string | React.ReactNode;
  options: ICascaderSelectOption[];
}

export interface ICascaderSelectProps extends IBaseProps {
  label?: string | React.ReactNode;
  value?: string;
  options?: ICascaderSelectOption[] | null;
  disabled?: boolean;
  clearable?: boolean;
  boxClassName?: string;
  overlayClassName?: string;
  emptyText?: string | React.ReactNode;
  searchPlaceholder?: string;
  firstLevelTitle?: string | React.ReactNode;
  secondLevelTitle?: string | React.ReactNode;
  levelTitles?: Array<string | React.ReactNode>;
  overallGroup?: ICascaderSelectOverallGroup;
  renderLeafContent?: (option: ICascaderSelectOption) => React.ReactNode;
  showChildrenCount?: boolean;
  footer?: React.ReactNode;
  button?: (context: ICascaderSelectChangeContext) => React.ReactNode;
  onChange?: (value: string, context: ICascaderSelectChangeContext) => void;
}

const CASCADER_SELECT_MIN_COLUMN_COUNT = 2;
const CASCADER_SELECT_COLUMN_WIDTH = 270;

const CascaderSelect: React.FC<ICascaderSelectProps> = (props) => {
  const {
    style,
    className,
    label,
    placeholder = CASCADER_SELECT_LOCALE_KEYS.placeholder,
    value,
    options = [],
    disabled = false,
    clearable = false,
    boxClassName,
    overlayClassName,
    emptyText = CASCADER_SELECT_LOCALE_KEYS.emptyText,
    searchPlaceholder = CASCADER_SELECT_LOCALE_KEYS.searchPlaceholder,
    firstLevelTitle = CASCADER_SELECT_LOCALE_KEYS.firstLevelTitle,
    secondLevelTitle = CASCADER_SELECT_LOCALE_KEYS.secondLevelTitle,
    levelTitles,
    overallGroup,
    renderLeafContent,
    showChildrenCount = true,
    footer,
    button,
    onChange,
  } = props;

  const { locale, formatMessage } = useIntl();
  const [search, setSearch] = useState('');
  const [activePath, setActivePath] = useState<string[]>([]);
  let labelText = label;

  if (label) {
    labelText = formatLocaleText(formatMessage, label);
  }

  const placeholderText = formatLocaleText(formatMessage, placeholder);
  const emptyTextContent = formatLocaleText(formatMessage, emptyText);
  const searchPlaceholderText = formatLocaleText(formatMessage, searchPlaceholder) as string;
  const firstLevelTitleText = formatLocaleText(formatMessage, firstLevelTitle);
  const secondLevelTitleText = formatLocaleText(formatMessage, secondLevelTitle);
  const overallTitleText = formatLocaleText(
    formatMessage,
    overallGroup?.title ?? CASCADER_SELECT_LOCALE_KEYS.overallTitle,
  );
  const optionList = options ?? [];
  const overallOptionList = (overallGroup?.options ?? []).filter((option) => option?.value);
  const hasOverallOptions = overallOptionList.length > 0;
  const realOptions = hasOverallOptions ? [...overallOptionList, ...optionList] : optionList;
  const {
    columns,
    activePath: realActivePath,
    selectedResult,
    currentContext,
  } = useCascaderSelect({
    formatMessage,
    options: realOptions,
    value,
    search,
    activePath,
  });
  const activeFirstLevelOption = columns?.[0]?.find((option) => option?.value === realActivePath?.[0]);
  const leafContent = activeFirstLevelOption && !hasOptionChildren(activeFirstLevelOption)
    ? renderLeafContent?.(activeFirstLevelOption)
    : null;
  const displayColumns = leafContent ? [...columns, []] : columns;
  const columnCount = Math.max(displayColumns.length, CASCADER_SELECT_MIN_COLUMN_COUNT);
  const contentWidth = columnCount * CASCADER_SELECT_COLUMN_WIDTH;

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSearch('');
    setActivePath([]);
    onChange?.('', EMPTY_CONTEXT);
  };

  const handleSelectOption = (option: ICascaderSelectOption, columnIndex: number): boolean => {
    const optionPath = getOptionPathFromColumns(columns, realActivePath, option, columnIndex);
    const children = option?.children || [];

    if (children.length > 0) {
      setActivePath(getCascadedActivePath(optionPath, children));
      return false;
    }

    setActivePath(optionPath?.map((item) => item?.value) || []);
    onChange?.(option?.value || '', getLeafChangeContext(option, optionPath));

    return true;
  };

  const getColumnTitle = (columnIndex: number): string | React.ReactNode => {
    return getCascaderColumnTitle({
      formatMessage,
      columnIndex,
      levelTitles,
      firstLevelTitle: firstLevelTitleText,
      secondLevelTitle: secondLevelTitleText,
      locale,
    });
  };

  const renderText = (text: string | React.ReactNode, className: string, disableEllipsisHover = false) => {
    const realText = getTextContent(text);

    if (!realText || disableEllipsisHover) {
      return (
        <span className={className} title={realText || undefined}>
          {text}
        </span>
      );
    }

    return <EllipsisHover className={className} text={realText} />;
  };

  const renderIcon = (option: ICascaderSelectOption) => {
    if (!option?.icon) {
      return null;
    }

    if (typeof option?.icon === 'string') {
      return <img className="dbh-cascader-select-option__icon" src={option?.icon} alt="" />;
    }

    return <span className="dbh-cascader-select-option__icon">{option?.icon}</span>;
  };

  const renderOptionContent = (option: ICascaderSelectOption, extra?: React.ReactNode) => {
    const content = (
      <div className="dbh-cascader-select-option__inner">
        {renderIcon(option)}
        {renderText(
          getOptionDisplayText(option, showChildrenCount),
          'dbh-cascader-select-option__text',
          hasOverallOptions,
        )}
        {extra && <span className="dbh-cascader-select-option__extra">{extra}</span>}
      </div>
    );

    return (
      <CommonBubble dark arrowPointAtCenter closeOnScroll content={option.tooltip}>
        {content}
      </CommonBubble>
    );
  };

  const renderButton = () => {
    if (typeof button === 'function') {
      return button(currentContext);
    }

    const displayValue = selectedResult?.option?.text || placeholderText;

    return (
      <div className="dbh-cascader-select-button">
        {labelText && <span className="dbh-cascader-select-button__label">{labelText}</span>}
        {renderText(displayValue, 'dbh-cascader-select-button__value')}
        {clearable && value && !disabled && (
          <Icon className="dbh-cascader-select-button__clear" type="dismiss" onClick={handleClear} />
        )}
      </div>
    );
  };

  return (
    <Dropdown
      style={style}
      button={renderButton()}
      className={classNames('dbh-cascader-select', className, {
        'is-disabled': disabled,
        'dbh-cascader-select--has-value': !!value,
      })}
      size="m"
      appearance="button"
      clickClose={false}
      closeOnScroll
      boxClassName={classNames('dbh-cascader-select-container', boxClassName)}
      overlayClassName={classNames('dbh-cascader-select-box', overlayClassName)}
    >
      {(close: () => void) => (
        <>
          <div className="dbh-cascader-select-search-container">
            <div className="dbh-cascader-select-search-wrap">
              <Input
                placeholder={searchPlaceholderText}
                value={search}
                className="dbh-cascader-select-search-input"
                type="text"
                onChange={(val) => {
                  setSearch(val);
                  setActivePath([]);
                }}
              />
              <div className="dbh-cascader-select-search-icon">
                {search && (
                  <Icon
                    type="dismiss"
                    onClick={() => {
                      setSearch('');
                      setActivePath([]);
                    }}
                  />
                )}
                <Icon type="search" />
              </div>
            </div>
          </div>
          <div className="dbh-cascader-select-content-container" style={{ width: contentWidth }}>
            {displayColumns?.map((column, columnIndex) => {
              const isLeafDescriptionColumn = !!(leafContent && columnIndex === columns.length);
              const levelOptions = columnIndex === 0 && hasOverallOptions
                ? column.filter((option) => !overallOptionList.some((overallOption) => overallOption.value === option.value))
                : column;
              const columnSections = isLeafDescriptionColumn
                ? []
                : columnIndex === 0 && hasOverallOptions
                  ? [
                    { key: 'overall', title: overallTitleText, options: overallOptionList },
                    { key: 'level', title: getColumnTitle(columnIndex), options: levelOptions },
                  ]
                  : [{ key: 'level', title: getColumnTitle(columnIndex), options: levelOptions }];

              return (
                <div className="dbh-cascader-select-content-wrap" key={`column-${columnIndex}`}>
                  {isLeafDescriptionColumn && (
                    <div className="dbh-cascader-select-item-none dbh-cascader-select-item-none--description">
                      {leafContent}
                    </div>
                  )}
                  {columnSections.map((section) => (
                    <React.Fragment key={section.key}>
                      <div className="dbh-cascader-select-content-title-wrap">
                        <span className="dbh-cascader-select-content-title">{section.title}</span>
                      </div>
                      {section.options?.length > 0 && (
                        <div className="dbh-cascader-select-item-wrap">
                          {section.options.map((option) => {
                            const isDisabled = !!option?.disabled;
                            const isSelected = option?.value === value;
                            const hasChildren = hasOptionChildren(option);
                            const isActive = realActivePath?.[columnIndex] === option?.value
                              || (!hasOverallOptions && isSelected);
                            let extra: React.ReactNode = null;

                            if (hasChildren) {
                              extra = <Icon type="arrowright" />;
                            }

                            return (
                              <div
                                key={option?.value}
                                className={classNames('dbh-cascader-select-option', {
                                  active: isActive,
                                  disabled: isDisabled,
                                })}
                                onClick={(e) => {
                                  e.stopPropagation();

                                  if (isDisabled) {
                                    return;
                                  }

                                  const isLeafSelected = handleSelectOption(option, columnIndex);

                                  if (isLeafSelected) {
                                    close();
                                  }
                                }}
                              >
                                {renderOptionContent(option, extra)}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                  {!column?.length && !isLeafDescriptionColumn && (
                    <div className="dbh-cascader-select-item-none">{emptyTextContent}</div>
                  )}
                </div>
              );
            })}
            {!columns?.length && (
              <div className="dbh-cascader-select-content-wrap dbh-cascader-select-content-wrap--empty">
                <div className="dbh-cascader-select-item-none">{emptyTextContent}</div>
              </div>
            )}
          </div>
          {footer && <div className="dbh-cascader-select-footer">{footer}</div>}
        </>
      )}
    </Dropdown>
  );
};

export default CascaderSelect;
